// uploadStore.ts
import { create } from 'zustand';
import { QueryClient } from '@tanstack/react-query';
import { uploadMediaToPresignedUrl, getPreSignedPrivateMediaUploadUrl } from '@/services/privateMedia/api';
import { QUERY_KEYS } from '@/consts/common';
import { withSafeResultAsync } from '@/utils/helpers/safeResult';
import { getErrorMessage, getI18nMessage } from '@/utils/helpers/i18nHelper';

export interface IUploadFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'cancelled';
  error?: string;
  cancelFn?: () => void;
}

interface IUploadStore {
  uploads: IUploadFile[];
  isManagerOpen: boolean;
  addUploads: (files: File[], queryClient: QueryClient) => Promise<void>;
  updateUploadProgress: (id: string, progress: number) => void;
  updateUploadStatus: (id: string, status: IUploadFile['status'], error?: string) => void;
  removeUpload: (id: string) => void;
  cancelUpload: (id: string) => void;
  retryUpload: (id: string, queryClient: QueryClient) => void;
  clearCompleted: () => void;
  toggleManager: () => void;
  closeManager: () => void;
  openManager: () => void;
}

const useUploadStore = create<IUploadStore>((set, get) => ({
  uploads: [],
  isManagerOpen: false,

  addUploads: async (files, queryClient) => {
    // Generate upload items with unique IDs
    const newUploads: IUploadFile[] = files.map((file) => ({
      id: `${file.name}-${Date.now()}`,
      file,
      progress: 0,
      status: 'pending' as const,
    }));

    // Adds to store and open manager
    set((state) => ({
      uploads: [...state.uploads, ...newUploads],
      isManagerOpen: true,
    }));

    // TODO: refactor this function to be more readable
    // Start parallel uploads
    newUploads.forEach(async (uploadFile) => {
      const startUpload = async () => {
        set((state) => ({
          uploads: state.uploads?.map((upload) => (upload.id === uploadFile.id
            ? {
              ...upload,
              status: 'uploading' as const,
              progress: 0,
              error: undefined,
            }
            : upload)),
        }));

        const getPresignedUrlResult = await withSafeResultAsync(
          () => getPreSignedPrivateMediaUploadUrl(uploadFile.file.name),
        );

        if (!getPresignedUrlResult.ok) {
          const errorMessage = getErrorMessage(getPresignedUrlResult.error);
          get().updateUploadStatus(uploadFile.id, 'failed', errorMessage);
          return;
        }

        const uploadMediaToPresignedUrlResult = await withSafeResultAsync(
          () => uploadMediaToPresignedUrl(
            uploadFile.file,
            getPresignedUrlResult.data,
            (progress) => {
              get().updateUploadProgress(uploadFile.id, progress);
            },
            (cancelFn) => {
              set((state) => ({
                uploads: state.uploads?.map((
                  upload,
                ) => (upload.id === uploadFile.id ? { ...upload, cancelFn } : upload)),
              }));
            },
          ),
        );

        if (!uploadMediaToPresignedUrlResult.ok) {
          const isUploadCancelled = uploadMediaToPresignedUrlResult?.error?.message === 'canceled';
          if (isUploadCancelled) {
            const errorMessage = getI18nMessage('common', 'UPLOAD_MANAGER.ERROR.UPLOAD_CANCELLED_BY_USER', 'Upload cancelled by user');
            get().updateUploadStatus(uploadFile.id, 'cancelled', errorMessage);
          } else {
            const errorMessage = getErrorMessage(uploadMediaToPresignedUrlResult.error);
            get().updateUploadStatus(uploadFile.id, 'failed', errorMessage);
          }
          return;
        }

        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_MEDIA_INFINITE] });
        }, 2000);
        get().updateUploadStatus(uploadFile.id, 'completed');
      };

      startUpload();
    });
  },

  updateUploadProgress: (id: string, progress: number) => {
    set((state) => ({
      uploads: state.uploads.map((upload) => (upload.id === id ? { ...upload, progress } : upload)),
    }));
  },

  updateUploadStatus: (id: string, status: IUploadFile['status'], error?: string) => {
    set((state) => ({
      uploads: state.uploads.map((upload) => (upload.id === id ? {
        ...upload, status, error, cancelFn: undefined,
      } : upload)),
    }));
  },

  removeUpload: (id: string) => {
    set((state) => ({
      uploads: state.uploads.filter((upload) => upload.id !== id),
    }));
  },

  cancelUpload: (id: string) => {
    const upload = get().uploads.find((u) => u.id === id);
    if (upload && upload.cancelFn) {
      upload.cancelFn();
    }
  },

  retryUpload: (id: string, queryClient: QueryClient) => {
    const upload = get().uploads.find((u) => u.id === id);
    if (!upload) return;

    const startUpload = async () => {
      get().updateUploadStatus(id, 'uploading');
      get().updateUploadProgress(id, 0);

      const getPresignedUrlResult = await withSafeResultAsync(
        () => getPreSignedPrivateMediaUploadUrl(upload.file.name),
      );

      if (!getPresignedUrlResult.ok) {
        const errorMessage = getErrorMessage(getPresignedUrlResult.error);
        get().updateUploadStatus(id, 'failed', errorMessage);
        return;
      }

      const uploadMediaToPresignedUrlResult = await withSafeResultAsync(
        () => uploadMediaToPresignedUrl(
          upload.file,
          getPresignedUrlResult.data,
          (progress) => {
            get().updateUploadProgress(id, progress);
          },
          (cancelFn) => {
            set((state) => ({
              uploads: state.uploads.map((u) => (u.id === id ? { ...u, cancelFn } : u)),
            }));
          },
        ),
      );
      if (!uploadMediaToPresignedUrlResult.ok) {
        const isUploadCancelled = uploadMediaToPresignedUrlResult?.error?.message === 'canceled';
        if (isUploadCancelled) {
          const errorMessage = getI18nMessage('common', 'UPLOAD_MANAGER.ERROR.UPLOAD_CANCELLED_BY_USER', 'Upload cancelled by user');
          get().updateUploadStatus(id, 'cancelled', errorMessage);
        } else {
          const errorMessage = getErrorMessage(uploadMediaToPresignedUrlResult.error);
          get().updateUploadStatus(id, 'failed', errorMessage);
        }
        return;
      }

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MY_MEDIA_INFINITE] });
      }, 2000);
      get().updateUploadStatus(id, 'completed');
    };

    startUpload();
  },

  clearCompleted: () => {
    set((state) => ({
      uploads: state.uploads.filter(
        (upload) => upload.status !== 'completed'
          && upload.status !== 'failed'
          && upload.status !== 'cancelled',
      ),
    }));
  },

  toggleManager: () => {
    set((state) => ({
      isManagerOpen: !state.isManagerOpen,
    }));
  },

  closeManager: () => {
    set({ isManagerOpen: false });
  },

  openManager: () => {
    set({ isManagerOpen: true });
  },
}));

export default useUploadStore;
